import React from 'react';
import './HowToPlay.css';

const DEMO_ROW_GRID = [
  [1, 0, 1, 0, 1, 0],
  [0, 1, 1, 0, 0, 1],
  [1, 0, 0, 1, 1, 0],
  [1, 1, 0, 0, 1, 1],
  [1, 1, 1, 1, 1, 1], // full row
];

const DEMO_COL_GRID = [
  [1, 1, 0, 1, 0, 0],
  [0, 1, 1, 0, 1, 0],
  [1, 1, 0, 1, 0, 1],
  [0, 1, 0, 0, 1, 0],
  [1, 1, 1, 0, 0, 1],
];

const HowToPlay = ({ onClose }) => {
  return (
    <div
      className="howtoplay-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="How to Play Tenfinity"
    >
      <div className="howtoplay-modal" onClick={e => e.stopPropagation()}>
        <button className="howtoplay-close" onClick={onClose} aria-label="Close">✕</button>
        <h2 className="howtoplay-title">How to Play</h2>

        <div className="howtoplay-body">

          <section className="howtoplay-section">
            <h3>🎯 The Goal</h3>
            <p>
              Place shapes onto the 10×10 grid to fill complete rows or columns.
              Filled lines vanish and score you points. Keep placing until no shape can fit — then it's game over.
            </p>
          </section>

          <section className="howtoplay-section">
            <h3>🧩 Placing Shapes</h3>
            <p>
              Three shapes appear at the bottom each round. <strong>Drag</strong> a shape onto the grid,
              or <strong>tap it</strong> to select it then tap where you want it to land.
            </p>
            <p>
              A green preview shows where it will land. A red preview means the spot's taken —
              find a different location. All three shapes must be placed before a new set appears.
            </p>
          </section>

          <section className="howtoplay-section">
            <h3>✨ Clearing Lines</h3>
            <p>
              Fill every cell in a <em>row</em> <strong>or</strong> <em>column</em> to clear it —
              both directions count. Clearing multiple lines at once earns a combo bonus.
            </p>
            <div className="howtoplay-demo-row">
              <div className="howtoplay-demo">
                <p className="demo-label">Full row → clears</p>
                <div className="mini-grid" style={{ gridTemplateColumns: 'repeat(6, 1fr)' }}>
                  {DEMO_ROW_GRID.map((row, r) =>
                    row.map((cell, c) => (
                      <div
                        key={`row-${r}-${c}`}
                        className={`mini-cell ${cell ? 'filled' : ''} ${r === 4 && cell ? 'clearing' : ''}`}
                      />
                    ))
                  )}
                </div>
              </div>
              <div className="howtoplay-demo">
                <p className="demo-label">Full column → clears</p>
                <div className="mini-grid" style={{ gridTemplateColumns: 'repeat(6, 1fr)' }}>
                  {DEMO_COL_GRID.map((row, r) =>
                    row.map((cell, c) => (
                      <div
                        key={`col-${r}-${c}`}
                        className={`mini-cell ${cell ? 'filled' : ''} ${c === 1 && cell ? 'clearing' : ''}`}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="howtoplay-section">
            <h3>🌡️ Difficulty</h3>
            <div className="difficulty-list">
              <div className="diff-item">
                <span className="diff-badge diff-easy">Easy</span>
                <span>Small, simple shapes. Great starting point.</span>
              </div>
              <div className="diff-item">
                <span className="diff-badge diff-normal">Normal</span>
                <span>Classic tetromino-style shapes — T, L, Z and more.</span>
              </div>
              <div className="diff-item">
                <span className="diff-badge diff-hard">Hard</span>
                <span>Complex multi-block shapes with awkward patterns.</span>
              </div>
              <div className="diff-item">
                <span className="diff-badge diff-extreme">Extreme</span>
                <span>Everything combined. You've been warned.</span>
              </div>
            </div>
          </section>

          <section className="howtoplay-section">
            <h3>💡 Tips</h3>
            <ul className="tips-list">
              <li>Try to clear rows <strong>and</strong> columns simultaneously for combo bonuses.</li>
              <li>Leave room for large shapes — don't corner yourself early.</li>
              <li>Use <strong>Undo</strong> to recover from a bad placement (once per turn).</li>
              <li>Save before a risky move — load it back if things go wrong.</li>
            </ul>
          </section>

          <section className="howtoplay-section">
            <h3>⌨️ Keyboard Shortcuts</h3>
            <div className="key-list">
              <div className="key-item"><kbd>R</kbd><span>Restart game</span></div>
              <div className="key-item"><kbd>H</kbd><span>Open this help</span></div>
              <div className="key-item"><kbd>S</kbd><span>Save game</span></div>
              <div className="key-item"><kbd>Esc</kbd><span>Close menu</span></div>
            </div>
          </section>

        </div>

        <button className="howtoplay-got-it" onClick={onClose}>
          Got it — let's play!
        </button>
      </div>
    </div>
  );
};

export default HowToPlay;
